import { stepSummary } from "@vidario/domain";
import { Check } from "lucide-react";
import { type ReactElement } from "react";

import { cn } from "../utils/cn";

export const PROCESS_STEPS = [
  "Kandidatensuche",
  "Longlist",
  "Qualifizierung",
  "Empfehlung",
  "Platziert",
] as const;
export type ProcessStep = (typeof PROCESS_STEPS)[number];

export interface ProcessStepperProps {
  /** Zero-based index of the current step. Steps before it are marked done. */
  currentStep: number;
  size?: "sm" | "md";
  className?: string;
  steps?: readonly string[];
}

export function ProcessStepper({
  currentStep,
  size = "md",
  className,
  steps = PROCESS_STEPS,
}: ProcessStepperProps): ReactElement {
  const compact = size === "sm";
  const stepper = (
    <ol
      aria-label="Prozessfortschritt"
      className={cn(
        "flex w-full items-start",
        compact ? "gap-0.5" : "gap-2",
        // With the phone line below, the wrapper takes the caller's classes.
        (compact || !stepSummary(steps, currentStep)) && className,
      )}
    >
      {steps.map((label, i) => {
        const isDone = i < currentStep;
        const isCurrent = i === currentStep;
        const isLast = i === steps.length - 1;
        const isFirst = i === 0;
        const reachedOrActive = isDone || isCurrent;

        return (
          <li
            key={label}
            aria-current={isCurrent ? "step" : undefined}
            className="flex min-w-0 flex-1 flex-col items-center"
          >
            <div className="flex w-full items-center">
              <span
                aria-hidden="true"
                className={cn(
                  "h-0.5 flex-1",
                  isFirst
                    ? "bg-transparent"
                    : reachedOrActive
                      ? "bg-primary-500"
                      : "bg-neutral-200",
                )}
              />
              <span
                className={cn(
                  "inline-flex shrink-0 items-center justify-center rounded-full border-2",
                  compact ? "h-2.5 w-2.5" : "h-3.5 w-3.5",
                  isDone
                    ? "border-primary-500 bg-primary-500 text-neutral-0"
                    : isCurrent
                      ? "border-primary-500 bg-neutral-0"
                      : "border-neutral-300 bg-neutral-0",
                )}
              >
                {isDone && !compact && <Check size={8} strokeWidth={3} />}
                {isCurrent && !compact && (
                  <span className="block h-1.5 w-1.5 rounded-full bg-primary-500" />
                )}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  "h-0.5 flex-1",
                  isLast ? "bg-transparent" : isDone ? "bg-primary-500" : "bg-neutral-200",
                )}
              />
            </div>
            {!compact && (
              // On phones the labels don't fit: screen readers keep them, the line below shows the current one (#163).
              <span
                className={cn(
                  "mt-2 max-w-full truncate text-center text-[11px] max-sm:sr-only",
                  reachedOrActive ? "text-primary-700" : "text-neutral-500",
                  isCurrent ? "font-semibold" : "font-medium",
                )}
              >
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
  const summary = compact ? "" : stepSummary(steps, currentStep);
  if (!summary) return stepper;
  return (
    <div className={cn("w-full", className)}>
      {stepper}
      <p
        aria-hidden="true"
        className="mt-2 mb-0 text-center text-xs font-semibold text-primary-700 sm:hidden"
      >
        {summary}
      </p>
    </div>
  );
}
